import React, { useState, useMemo } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import { formatPhoneNumber, validatePhoneNumber } from "@/utils/phoneValidation";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Skeleton } from "@/components/ui/Skeleton";
import { CallButton } from "@/components/ui/CallButton";

export type PhoneType = "cell" | "home" | "work";

export type PhoneData = {
  type: PhoneType;
  number: string;
};

export interface PhoneEditorProps {
  phones: PhoneData[];
  onChange?: (phones: PhoneData[]) => void;
  editing?: boolean;
  loading?: boolean;
  /** Error message for cell phone uniqueness check */
  cellPhoneError?: string;
  /** Whether cell phone uniqueness check is in progress */
  cellPhoneChecking?: boolean;
}

const PHONE_TYPE_CONFIG: Record<PhoneType, { color: string; icon: string; label: string }> = {
  cell: { color: "#10b981", icon: "cellphone", label: "Cell" },
  home: { color: "#3b82f6", icon: "home", label: "Home" },
  work: { color: "#8b5cf6", icon: "briefcase", label: "Work" },
};

export function PhoneEditor({
  phones,
  onChange,
  editing = false,
  loading = false,
  cellPhoneError,
  cellPhoneChecking,
}: PhoneEditorProps) {
  const { colors } = useTheme();
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const disabled = loading || !editing;

  // Calculate which phone types are available (not already used)
  const usedTypes = useMemo(() => new Set(phones.map((p) => p.type)), [phones]);

  const getAvailableTypes = (currentType: PhoneType): { text: string; bgColor: string }[] => {
    const allTypes: PhoneType[] = ["cell", "home", "work"];
    return allTypes
      .filter((type) => type === currentType || !usedTypes.has(type))
      .map((type) => ({
        text: PHONE_TYPE_CONFIG[type].label,
        bgColor: PHONE_TYPE_CONFIG[type].color,
      }));
  };

  const handlePhoneUpdate = (index: number, field: keyof PhoneData, value: string) => {
    if (!editing || !onChange) return;

    const updatedPhones = [...phones];
    if (field === "number") {
      const formatted = formatPhoneNumber(value);
      const isValid = validatePhoneNumber(formatted);

      if (!isValid && formatted.length > 0) {
        setValidationErrors({ ...validationErrors, [index]: "Invalid phone number" });
      } else {
        const newErrors = { ...validationErrors };
        delete newErrors[index];
        setValidationErrors(newErrors);
      }

      updatedPhones[index] = { ...updatedPhones[index], number: formatted };
    } else if (field === "type") {
      // Map label back to type
      const typeEntry = Object.entries(PHONE_TYPE_CONFIG).find(([, config]) => config.label === value);
      if (typeEntry) {
        updatedPhones[index] = { ...updatedPhones[index], type: typeEntry[0] as PhoneType };
      }
    }
    onChange(updatedPhones);
  };

  const handleRemove = (index: number) => {
    if (!editing || !onChange) return;
    const newPhones = phones.filter((_, i) => i !== index);
    const newErrors = { ...validationErrors };
    delete newErrors[index];
    setValidationErrors(newErrors);
    onChange(newPhones);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.phoneRow}>
          <Skeleton width={60} height={40} />
          <Skeleton width="100%" height={40} style={{ flex: 1 }} />
        </View>
      </View>
    );
  }

  if (!phones || phones.length === 0) {
    return <ThemedText style={{ opacity: 0.5 }}>No phones</ThemedText>;
  }

  return (
    <View style={styles.container}>
      {phones.map((phone, index) => (
        <View key={index}>
          <View style={styles.phoneRow}>
            {/* Type - Badge in view mode, dropdown in edit mode */}
            {editing ? (
              <ThemedInput
                value={PHONE_TYPE_CONFIG[phone.type].label}
                onChangeText={(value) => handlePhoneUpdate(index, "type", value)}
                items={getAvailableTypes(phone.type)}
                style={styles.typeInput}
                editable={!disabled}
              />
            ) : (
              <View style={[styles.typeBadge, { backgroundColor: PHONE_TYPE_CONFIG[phone.type].color }]}>
                <MaterialCommunityIcons name={PHONE_TYPE_CONFIG[phone.type].icon as any} size={14} color="white" />
                <ThemedText style={styles.typeBadgeText}>{PHONE_TYPE_CONFIG[phone.type].label}</ThemedText>
              </View>
            )}

            {/* Phone number */}
            <ThemedInput
              value={phone.number}
              onChangeText={(value) => handlePhoneUpdate(index, "number", value)}
              placeholder={disabled ? "—" : "(555) 555-5555"}
              keyboardType="phone-pad"
              style={styles.numberInput}
              editable={!disabled}
            />

            {/* Action button - Call menu in view mode, remove in edit mode */}
            {editing ? (
              <Pressable
                onPress={() => handleRemove(index)}
                style={[styles.actionButton, { backgroundColor: colors.dangerBackground }]}
              >
                <MaterialCommunityIcons name="close" size={18} color={colors.dangerText} />
              </Pressable>
            ) : (
              <CallButton phoneNumber={phone.number} size={36} />
            )}
          </View>
          {validationErrors[index] && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{validationErrors[index]}</ThemedText>
          )}
          {/* Show cell phone uniqueness error */}
          {phone.type === "cell" && cellPhoneError && !validationErrors[index] && (
            <View style={styles.cellErrorRow}>
              <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                {cellPhoneChecking ? "Checking..." : cellPhoneError}
              </ThemedText>
            </View>
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  phoneRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  typeInput: {
    width: 90,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    minWidth: 80,
    justifyContent: "center",
  },
  typeBadgeText: {
    color: "white",
    fontSize: 13,
    fontWeight: "600",
  },
  numberInput: {
    flex: 1,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    fontSize: 12,
    marginTop: 2,
    marginLeft: 98, // Align with number input
  },
  cellErrorRow: {
    flexDirection: "row",
    alignItems: "center",
  },
});
